/* eslint-disable jsx-a11y/anchor-is-valid */
import { useEffect, useState } from 'react';
import * as Yup from 'yup';
import { Link, useParams, useLocation } from 'react-router-dom';
import { useFormik } from 'formik';
import { getRanks, getUserByToken, login, shipLogin, getVesselsByCompany } from '../core/_requests';
import { useAuth } from '../core/Auth';
import { Eye, EyeOff, Loader2, LogIn, AlertCircle, User, Lock, Ship } from 'lucide-react';
import axios from 'axios';
import { Rank } from '../../Crewing/core/_models';
import { Vessel } from '../core/_models';
import SearchableSelect from '../../../shared/components/SearchableSelect'

const shoreLoginSchema = Yup.object().shape({
  username: Yup.string()
    .min(3, 'Minimum 3 symbols')
    .max(50, 'Maximum 50 symbols')
    .required('Username is required'),
  password: Yup.string()
    .min(3, 'Minimum 3 symbols')
    .max(50, 'Maximum 50 symbols')
    .required('Password is required'),
});

const shipLoginSchema = Yup.object().shape({
  vesselId: Yup.string().required('Vessel is required'),
  rank: Yup.string().required('Rank is required'),
  password: Yup.string()
    .min(3, 'Minimum 3 symbols')
    .max(50, 'Maximum 50 symbols')
    .required('Password is required'),
});

export function TabbedLogin() {
  const { companyUsername } = useParams<{ companyUsername: string }>();
  const [activeTab, setActiveTab] = useState<'shore' | 'ship'>('shore');
  const [loading, setLoading] = useState(false);
  const [vessels, setVessels] = useState<Vessel[]>([]);
  const [ranks, setRanks] = useState<Rank[]>([]);
  const [selectedVessel, setSelectedVessel] = useState<Vessel | null>(null);
  const { saveAuth, setCurrentUser } = useAuth();
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    if (companyUsername) {
      fetchVessels();
    }
  }, [companyUsername]);

  const location = useLocation();

  // Seed companyUsername from the route so redirect will work even before /profile/me finishes
  useEffect(() => {
    if (companyUsername) {
      sessionStorage.setItem('companyUsername', companyUsername);
    } else {
      sessionStorage.removeItem('companyUsername');
    }
  }, [companyUsername]);

  useEffect(() => {
    if (location.pathname.endsWith('/ship-login')) {
      setActiveTab('ship');
    }
  }, [location]);

  useEffect(() => {
    if (selectedVessel?.imoNumber) {
      fetchRanks(selectedVessel.imoNumber);
    }
  }, [selectedVessel]);

  const fetchVessels = async () => {
    try {
      const vesselsList = await getVesselsByCompany(companyUsername!);
      setVessels(vesselsList);
    } catch (err) {
      console.error('Failed to fetch vessels:', err);
    }
  };

  const fetchRanks = async (imoNumber: string) => {
    try {
      const ranksList = await getRanks(imoNumber);
      setRanks(ranksList);
    } catch (err) {
      console.error('Failed to fetch ranks:', err);
    }
  };

  // Shore Formik
  const shoreFormik = useFormik({
    initialValues: { username: '', password: '' },
    validationSchema: shoreLoginSchema,
    onSubmit: async (values, { setStatus, setSubmitting }) => {
      setLoading(true);
      try {
        const { data: auth } = await login(values.username, values.password);
        saveAuth(auth);
        const roleId = auth.role?.id;
        const roleEntityId = auth.roleEntityId;
        const roleEntityName = auth.roleEntityName;
        const userName = auth.userDetails?.username;
        if (roleId) sessionStorage.setItem('roleId', roleId.toString());
        if (userName) sessionStorage.setItem('userName', userName.toString());
        if (roleEntityId) sessionStorage.setItem('roleEntityId', roleEntityId.toString());
        if (roleEntityName) sessionStorage.setItem('roleEntityName', roleEntityName.toString());
        const { data: user } = await getUserByToken();
        setCurrentUser(user);
      } catch (error: any) {
        const status = axios.isAxiosError(error) ? error.response?.status : undefined
        if (status === 401) {
          saveAuth(undefined);
        }
        let message = 'The login details are incorrect';
        if (axios.isAxiosError(error)) {
          if (status === 503 || (!error.response && error.request)) {
            message = 'Our service is temporarily unavailable for maintenance. Please try again in a few minutes.';
          }
        }
        setStatus(message);
        setSubmitting(false);
        setLoading(false);
      }
    },
  });

  // Ship Formik
  const shipFormik = useFormik({
    enableReinitialize: true,
    initialValues: { vesselId: '', rank: '', password: '' },
    validationSchema: shipLoginSchema,
    onSubmit: async (values, { setStatus, setSubmitting }) => {
      setLoading(true);
      if (!selectedVessel) return;
      try {
        const { data: auth } = await shipLogin(
          companyUsername!,
          selectedVessel.vesselType,
          selectedVessel.imoNumber,
          values.rank,
          values.password
        );
        saveAuth(auth);
        const roleId = auth.userDetails?.roleId;
        const vesselName = auth.userDetails?.vessel?.fleet_name;
        const vesselId = auth.userDetails?.vessel?.id;
        const userName = auth.userDetails?.username;
        const rank = auth.userDetails?.rank;
        const imoNumber = auth.userDetails?.vessel?.imoNumber;
        const companyAdminId = auth.userDetails?.vessel?.companyGroupAdminId;
        const companyId = auth.userDetails?.vessel?.companyAdminId;
        const vesselType = auth.userDetails?.vessel?.vesselType;
        if (roleId) localStorage.setItem('roleId', roleId.toString());
        if (vesselName) localStorage.setItem('vesselName', vesselName.toString());
        if (vesselType) localStorage.setItem('vesselType', vesselType.toString());
        if (vesselId) sessionStorage.setItem('vesselId', vesselId.toString());
        if (userName) sessionStorage.setItem('userName', userName.toString());
        if (rank) sessionStorage.setItem('rank', rank.toString());
        if (imoNumber) sessionStorage.setItem('imoNumber', imoNumber.toString());
        if (companyId) sessionStorage.setItem('companyId', companyId.toString());
        if (companyAdminId) sessionStorage.setItem('companyAdminId', companyAdminId.toString());
        const { data: user } = await getUserByToken();
        setCurrentUser(user);
      } catch (error: any) {
        const status = axios.isAxiosError(error) ? error.response?.status : undefined
        if (status === 401) {
          saveAuth(undefined);
        }
        let message = 'The login details are incorrect';
        if (axios.isAxiosError(error)) {
          if (status === 503 || (!error.response && error.request)) {
            message = 'Our service is temporarily unavailable for maintenance. Please try again in a few minutes.';
          }
        }
        setStatus(message);
        setSubmitting(false);
        setLoading(false);
      }
    },
  });

  const handleVesselChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const vessel = vessels.find(v => v.id.toString() === e.target.value);
    setSelectedVessel(vessel || null);
    shipFormik.setFieldValue('vesselId', e.target.value);
    setRanks([]);
    shipFormik.setFieldValue('rank', '');
  };

  return (
    <>
      <h2 className="text-2xl font-bold text-slate-900">Welcome back</h2>
      <p className="mt-1 text-sm text-slate-500">Sign in to your ShipsEdge workspace.</p>

      {/* Tab buttons */}
      <div className="mt-7 flex gap-2">
        <button
          type="button"
          onClick={() => setActiveTab('shore')}
          className={`flex-1 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors ${
            activeTab === 'shore'
              ? 'bg-blue-600 text-white shadow-sm'
              : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-50'
          }`}
        >
          Shore Login
        </button>
        <button
          type="button"
          onClick={() => setActiveTab('ship')}
          className={`flex-1 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors ${
            activeTab === 'ship'
              ? 'bg-blue-600 text-white shadow-sm'
              : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-50'
          }`}
        >
          <Ship className="w-4 h-4 inline-block mr-1.5 -mt-0.5" />
          Ship Login
        </button>
      </div>

      {/* Shore Login Form */}
      {activeTab === 'shore' && (
        <form onSubmit={shoreFormik.handleSubmit} noValidate id="kt_login_signin_form_shore" className="mt-5 space-y-4">
          {/* Error display */}
          {shoreFormik.status && (
            <div
              role="alert"
              className="flex items-start gap-2 px-3 py-2.5 rounded-lg border border-red-200 bg-red-50 text-xs text-red-700"
            >
              <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <span>{shoreFormik.status}</span>
            </div>
          )}

          {/* Username */}
          <div>
            <label htmlFor="username" className="block text-xs font-medium text-slate-700 mb-1.5">
              Username
            </label>
            <div className="relative">
              <User className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
              <input
                id="username"
                type="text"
                required
                autoComplete="username"
                placeholder="Enter your username"
                className="w-full pl-9 pr-3 py-2.5 text-sm rounded-lg border border-slate-200 bg-white shadow-sm placeholder:text-slate-400 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                {...shoreFormik.getFieldProps('username')}
              />
            </div>
            {shoreFormik.touched.username && shoreFormik.errors.username && (
              <p className="mt-1 text-xs text-red-600">{shoreFormik.errors.username}</p>
            )}
          </div>

          {/* Password */}
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label htmlFor="password" className="block text-xs font-medium text-slate-700">
                Password
              </label>
              <Link
                to="/auth/forgot-password"
                className="text-[11px] text-blue-600 hover:underline font-medium"
              >
                Forgot password?
              </Link>
            </div>
            <div className="relative">
              <Lock className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
              <input
                id="password"
                type={showPassword ? 'text' : 'password'}
                required
                autoComplete="current-password"
                placeholder="Enter your password"
                className="w-full pl-9 pr-10 py-2.5 text-sm rounded-lg border border-slate-200 bg-white shadow-sm placeholder:text-slate-400 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                {...shoreFormik.getFieldProps('password')}
              />
              <button
                type="button"
                aria-label={showPassword ? 'Hide password' : 'Show password'}
                onClick={() => setShowPassword((v) => !v)}
                className="absolute right-2 top-1/2 -translate-y-1/2 w-7 h-7 flex items-center justify-center rounded-md text-slate-400 hover:text-slate-600 hover:bg-slate-100"
              >
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            {shoreFormik.touched.password && shoreFormik.errors.password && (
              <p className="mt-1 text-xs text-red-600">{shoreFormik.errors.password}</p>
            )}
          </div>

          {/* Submit */}
          <button
            type="submit"
            disabled={loading}
            className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-blue-600 text-white text-sm font-semibold shadow-sm hover:bg-blue-700 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <LogIn className="w-4 h-4" />}
            {loading ? 'Signing in…' : 'Sign in'}
          </button>
        </form>
      )}

      {/* Ship Login Form */}
      {activeTab === 'ship' && (
        <form onSubmit={shipFormik.handleSubmit} noValidate id="kt_login_signin_form_ship" className="mt-5 space-y-4">
          {/* Error display */}
          {shipFormik.status && (
            <div
              role="alert"
              className="flex items-start gap-2 px-3 py-2.5 rounded-lg border border-red-200 bg-red-50 text-xs text-red-700"
            >
              <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <span>{shipFormik.status}</span>
            </div>
          )}

          {/* Vessel select */}
          <div>
            <label htmlFor="vessel" className="block text-xs font-medium text-slate-700 mb-1.5">
              Select Vessel
            </label>
            <div className="relative">
              <Ship className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
              <SearchableSelect
                id="vessel"
                onChange={handleVesselChange}
                value={shipFormik.values.vesselId}
                className="w-full pl-9 pr-3 py-2.5 text-sm rounded-lg border border-slate-200 bg-white shadow-sm focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 appearance-none"
              >
                <option value="">-- Select Vessel --</option>
                {vessels.map((v) => (
                  <option key={v.id} value={v.id}>
                    {v.fleet_name} {v.companyAdmin ? `(${v.companyAdmin.name})` : `(${v.companyGroupAdmin.name})`}
                  </option>
                ))}
              </SearchableSelect>
            </div>
            {shipFormik.touched.vesselId && shipFormik.errors.vesselId && (
              <p className="mt-1 text-xs text-red-600">{shipFormik.errors.vesselId}</p>
            )}
          </div>

          {/* Rank select */}
          <div>
            <label htmlFor="rank" className="block text-xs font-medium text-slate-700 mb-1.5">
              Select Rank
            </label>
            <SearchableSelect
              id="rank"
              className="w-full px-3 py-2.5 text-sm rounded-lg border border-slate-200 bg-white shadow-sm focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 disabled:opacity-50 disabled:cursor-not-allowed"
              {...shipFormik.getFieldProps('rank')}
              disabled={!selectedVessel}
            >
              <option value="">-- Select Rank --</option>
              {ranks.map((r) => (
                <option key={r.id} value={r.rank}>
                  {r.rank}
                </option>
              ))}
            </SearchableSelect>
            {shipFormik.touched.rank && shipFormik.errors.rank && (
              <p className="mt-1 text-xs text-red-600">{shipFormik.errors.rank}</p>
            )}
          </div>

          {/* Password */}
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label htmlFor="ship-password" className="block text-xs font-medium text-slate-700">
                Password
              </label>
              <Link
                to="/auth/forgot-password"
                className="text-[11px] text-blue-600 hover:underline font-medium"
              >
                Forgot password?
              </Link>
            </div>
            <div className="relative">
              <Lock className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
              <input
                id="ship-password"
                type={showPassword ? 'text' : 'password'}
                required
                autoComplete="new-password"
                placeholder="Enter your password"
                className="w-full pl-9 pr-10 py-2.5 text-sm rounded-lg border border-slate-200 bg-white shadow-sm placeholder:text-slate-400 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                {...shipFormik.getFieldProps('password')}
              />
              <button
                type="button"
                aria-label={showPassword ? 'Hide password' : 'Show password'}
                onClick={() => setShowPassword((v) => !v)}
                className="absolute right-2 top-1/2 -translate-y-1/2 w-7 h-7 flex items-center justify-center rounded-md text-slate-400 hover:text-slate-600 hover:bg-slate-100"
              >
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            {shipFormik.touched.password && shipFormik.errors.password && (
              <p className="mt-1 text-xs text-red-600">{shipFormik.errors.password}</p>
            )}
          </div>

          {/* Submit */}
          <button
            type="submit"
            disabled={loading}
            className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-blue-600 text-white text-sm font-semibold shadow-sm hover:bg-blue-700 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <LogIn className="w-4 h-4" />}
            {loading ? 'Signing in…' : 'Sign in'}
          </button>
        </form>
      )}

      <p className="mt-7 text-center text-sm text-slate-600">
        New to ShipsEdge?{' '}
        <Link to="/auth/registration" className="text-blue-600 font-semibold hover:underline">
          Create an account
        </Link>
      </p>
    </>
  );
}
